/* eslint-disable @typescript-eslint/no-explicit-any */
import { FetchPolicy } from '@apollo/client';
import { Severity } from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ALL_DONE, MKT_HOME, NEXT_STEPS } from '../constants/urls';
import { ErrorCodeEnum, TemporaryInfo, useTemporaryInfoLazyQuery } from '../gql/generated/graphql';
import hotjar from '../services/analytics/hotjar';
import { logger } from '../services/sentry';
import { resetAuth } from '../utils/auth';
import { CookieKeys, useCookie } from './useCookie';

const ALLOW_IMPORTED_PATHS = [NEXT_STEPS, ALL_DONE];

// TODO-JK: Remove other references to the temporaryInfo query
export const useTempInfo = ({
  fetchPolicy,
  refresh,
}: {
  fetchPolicy?: FetchPolicy;
  refresh?: boolean;
} = {}) => {
  const { pathname } = useLocation();
  const [getTempInfo] = useTemporaryInfoLazyQuery({
    context: {
      isErrorHandled: true,
    },
  });
  const [guid] = useCookie<string>(CookieKeys.GUID_KEY);
  const [info, setInfo] = useState<TemporaryInfo>();
  const [loading, setLoading] = useState<boolean>(true);
  const isMountedRef = useRef<boolean>(true);

  const isImportedAllowedPaths = useMemo(() => ALLOW_IMPORTED_PATHS.includes(pathname), [pathname]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchInfo = useCallback(async (): Promise<TemporaryInfo | undefined> => {
    if (!guid) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    try {
      const { data, error } = await getTempInfo({
        fetchPolicy: fetchPolicy || 'cache-first',
        variables: {
          id: guid as string,
          allowImported: isImportedAllowedPaths,
        },
      });

      if (isMountedRef.current && data?.temporaryInfo?.data) {
        setInfo(data.temporaryInfo);
        if (data.temporaryInfo.data?.vin) {
          hotjar.identify(data.temporaryInfo.data.vin, {
            temp_info_id: data.temporaryInfo.id || undefined,
            state: data.temporaryInfo.data?.license_plate_state || undefined,
          });
        }
      }

      if (error) {
        throw error;
      }

      return data?.temporaryInfo ?? undefined;
    } catch (error: any) {
      if (isMountedRef.current) {
        const isExpectedSessionError =
          error?.graphQLErrors?.[0]?.extensions?.code === ErrorCodeEnum.ResetGuidError;

        // Expected session resets are normal flow - log as warnings.
        logger.error(
          'useTempInfo.ts',
          'Error fetching info',
          null,
          error,
          isExpectedSessionError ? Severity.Warning : Severity.Error,
        );

        if (isExpectedSessionError) {
          resetAuth({ removeGuid: true });
          window.location.href = MKT_HOME;
        }
      }

      return undefined;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [guid, fetchPolicy, isImportedAllowedPaths]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo, refresh]);

  return { info, infoLoading: loading, refetch: fetchInfo };
};
